import { Button, Flex, TextAreaField, Typography } from '@iheartjane/reefer';

import { ActivitiesField } from '../activitiesField/activitiesField';
import { FeelingsField } from '../feelingsField/feelingsField';
import { RateAndReviewModal } from '../rateAndReviewModal/rateAndReviewModal';
import { isReviewSubmittable } from '../reviewFormData';
import type { ReviewFormData } from '../reviewFormData';
import type { ReviewOption } from '../reviewOptionsField/reviewOptionsField';
import { StarRatingField } from '../starRatingField/starRatingField';

import styles from '../rateAndReview.module.css';

export interface RateAndReviewFormProps {
  activityOptions: ReviewOption[];
  feelingOptions: ReviewOption[];
  isSubmitting?: boolean;
  onChange: (data: ReviewFormData) => void;
  onSubmit: () => void;
  testId?: string;
  value: ReviewFormData;
}

/**
 * Rate & Review body that shows every question at once (A/B test variant A).
 * Renders inside `RateAndReviewModal`, the caller owns the form state and the submit request.
 */
export function RateAndReviewForm({
  activityOptions,
  feelingOptions,
  isSubmitting = false,
  onChange,
  onSubmit,
  testId = 'rate-and-review-form',
  value,
}: RateAndReviewFormProps) {
  const update = (patch: Partial<ReviewFormData>) =>
    onChange({ ...value, ...patch });

  return (
    <>
      <RateAndReviewModal.Body>
        <Flex data-testid={testId} flexDirection="column" gap={32} width="100%">
          <div className={styles['primaryStars']}>
            <StarRatingField
              onChange={(rating) => update({ rating })}
              value={value.rating}
            />
          </div>
          <ActivitiesField
            onChange={(activities) => update({ activities })}
            options={activityOptions}
            selectedIds={value.activities}
          />
          <FeelingsField
            onChange={(feelings) => update({ feelings })}
            options={feelingOptions}
            selectedIds={value.feelings}
          />
          <TextAreaField
            disableMobileInputStyling
            label={
              <>
                Tell us about your experience.{' '}
                <Typography as="span" color="grays-mid">
                  (Optional)
                </Typography>
              </>
            }
            name="comment"
            onChange={(comment) => update({ comment })}
            resize={false}
            rows={5}
            typography="body-semibold"
            value={value.comment}
            width="100%"
          />
        </Flex>
      </RateAndReviewModal.Body>
      <RateAndReviewModal.Footer>
        <Button
          disabled={!isReviewSubmittable(value)}
          label="Submit review"
          loading={isSubmitting}
          onClick={onSubmit}
          className={styles['submit']}
        />
      </RateAndReviewModal.Footer>
    </>
  );
}
